'use client';
import React from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import type { GRN } from './GRNContent';

export default function GRNDetailPanel({ grn, onClose }: { grn: GRN; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-primary/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-2xl bg-card h-full overflow-y-auto scrollbar-thin shadow-modal flex flex-col fade-in">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-border bg-card sticky top-0 z-10">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono-data text-base font-700 text-foreground">{grn.grnNumber}</span>
              <StatusBadge status={grn.status as Parameters<typeof StatusBadge>[0]['status']} />
              {grn.status === 'APPROVED' && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Icon name="LockClosedIcon" size={12} />
                  Immutable
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{grn.vendor} · {grn.site}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted transition-colors">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>

        <div className="flex-1 px-6 py-5 space-y-6">
          {/* Meta */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { label: 'PO Reference', value: grn.poReference, mono: true },
              { label: 'Delivery Date', value: grn.deliveryDate },
              { label: 'Created Date', value: grn.createdDate },
              { label: 'Created By', value: grn.createdBy },
              { label: 'Total Value', value: `€ ${grn.totalValue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}`, bold: true },
              { label: 'Line Items', value: `${grn.lineItems.length} items` },
            ].map((m) => (
              <div key={`meta-${m.label}`}>
                <p className="text-[10px] font-600 uppercase tracking-wider text-muted-foreground">{m.label}</p>
                <p className={`text-sm mt-0.5 ${m.bold ? 'font-700 text-foreground' : m.mono ? 'font-mono-data text-foreground' : 'text-foreground'}`}>
                  {m.value}
                </p>
              </div>
            ))}
          </div>

          {/* Line items */}
          <div>
            <h4 className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-3">Line Items</h4>
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    {['Item', 'Ordered', 'Received', 'Rejected', 'Unit Cost', 'Total'].map((h) => (
                      <th key={`dp-th-${h}`} className="text-left text-[10px] font-600 uppercase tracking-wider text-muted-foreground px-3 py-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {grn.lineItems.map((li) => (
                    <tr key={li.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-3 py-2.5">
                        <p className="text-xs font-600 text-foreground">{li.itemName}</p>
                        <p className="text-[11px] font-mono-data text-muted-foreground">{li.itemCode}</p>
                        {li.batchNumber && (
                          <p className="text-[10px] text-muted-foreground">Batch: {li.batchNumber}</p>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs tabular-nums text-muted-foreground">{li.orderedQty} {li.uom}</td>
                      <td className="px-3 py-2.5 text-xs tabular-nums text-teal font-500">{li.receivedQty} {li.uom}</td>
                      <td className="px-3 py-2.5 text-xs tabular-nums text-red-600">{li.rejectedQty > 0 ? `${li.rejectedQty} ${li.uom}` : '—'}</td>
                      <td className="px-3 py-2.5 text-xs tabular-nums text-foreground">€ {li.unitCost.toFixed(2)}</td>
                      <td className="px-3 py-2.5 text-xs tabular-nums font-600 text-foreground">€ {li.totalCost.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-muted/30">
                    <td colSpan={5} className="px-3 py-2.5 text-xs font-600 text-right text-muted-foreground">Grand Total</td>
                    <td className="px-3 py-2.5 text-sm font-700 text-foreground tabular-nums">
                      € {grn.totalValue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Notes / status info */}
          {grn.inspectionNotes && (
            <div className="bg-secondary/5 border border-secondary/20 rounded-lg px-4 py-3">
              <p className="text-xs font-600 text-secondary mb-1 flex items-center gap-1.5">
                <Icon name="ClipboardDocumentCheckIcon" size={13} />
                Inspection Notes
              </p>
              <p className="text-sm text-foreground leading-relaxed">{grn.inspectionNotes}</p>
            </div>
          )}
          {grn.rejectionReason && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <p className="text-xs font-600 text-red-600 mb-1 flex items-center gap-1.5">
                <Icon name="ExclamationCircleIcon" size={13} />
                Rejection Reason
              </p>
              <p className="text-sm text-red-700 leading-relaxed">{grn.rejectionReason}</p>
            </div>
          )}
          {grn.approvedBy && (
            <div className="bg-teal/5 border border-teal/20 rounded-lg px-4 py-3 flex items-center gap-3">
              <Icon name="CheckCircleIcon" size={18} className="text-teal shrink-0" />
              <div>
                <p className="text-xs font-600 text-teal">Approved by {grn.approvedBy}</p>
                <p className="text-xs text-muted-foreground">{grn.approvedDate} · Weighted average costs updated · Stock positions updated</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-border bg-muted/20 flex items-center gap-3">
          {grn.status === 'APPROVED' && (
            <button
              onClick={() => toast.info(`Generating GRN PDF for ${grn.grnNumber}…`)}
              className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg text-sm font-500 hover:bg-secondary/90 transition-all active:scale-95"
            >
              <Icon name="ArrowDownTrayIcon" size={15} />
              Download PDF
            </button>
          )}
          <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted transition-colors ml-auto">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}